import React from "react";
import { Eye, Users, Calendar, TrendingUp, ExternalLink } from "lucide-react";
import { api } from "../../lib/api";

const PATH_LABEL = {
    "/": "Accueil",
    "/galerie": "Galeries",
    "/a-propos": "À propos",
    "/tarifs": "Tarifs",
    "/temoignages": "Témoignages",
    "/contact": "Contact",
};

function StatCard({ icon: Icon, label, value, sub, testId }) {
    return (
        <div data-testid={testId} className="border border-white/10 bg-[#0f0f0f] p-6">
            <div className="flex items-center justify-between">
                <p className="text-xs uppercase tracking-[0.22em] text-zinc-500">{label}</p>
                <Icon size={18} strokeWidth={1.5} className="text-[#c5a059]" />
            </div>
            <p className="text-4xl font-semibold tracking-tight mt-3">{value}</p>
            {sub && <p className="text-xs text-zinc-500 mt-1">{sub}</p>}
        </div>
    );
}

export default function StatsPanel() {
    const [data, setData] = React.useState(null);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState("");

    const load = React.useCallback(() => {
        setLoading(true);
        api.get("/admin/analytics")
            .then((r) => setData(r.data))
            .catch(() => setError("Impossible de charger les statistiques."))
            .finally(() => setLoading(false));
    }, []);

    React.useEffect(() => { load(); }, [load]);

    if (loading) return <p className="text-zinc-500">Chargement des statistiques…</p>;
    if (error) return <p className="text-red-400">{error}</p>;
    if (!data) return null;

    // Build a sparkline-friendly view of last 30 days
    const max = Math.max(1, ...data.per_day.map((d) => d.count));

    return (
        <div data-testid="stats-panel" className="space-y-10">
            <div className="flex items-end justify-between flex-wrap gap-3">
                <div>
                    <h2 className="font-serif-display text-2xl">Statistiques du site</h2>
                    <p className="text-zinc-500 text-sm mt-1">Vue d'ensemble des visites — chiffres en temps réel.</p>
                </div>
                <button
                    data-testid="stats-refresh"
                    onClick={load}
                    className="border border-white/15 px-4 py-2 text-xs uppercase tracking-[0.2em] hover:bg-white hover:text-black transition-colors"
                >
                    Rafraîchir
                </button>
            </div>

            {/* Top stats */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard testId="stat-today" icon={Calendar} label="Aujourd'hui" value={data.visits_today} sub="vues de pages" />
                <StatCard testId="stat-7d" icon={TrendingUp} label="7 derniers jours" value={data.visits_7d} sub={`${data.unique_7d} visiteurs uniques`} />
                <StatCard testId="stat-30d" icon={Eye} label="30 derniers jours" value={data.visits_30d} sub={`${data.unique_30d} visiteurs uniques`} />
                <StatCard testId="stat-total" icon={Users} label="Total depuis le lancement" value={data.total_visits} sub={`${data.unique_total} visiteurs uniques`} />
            </div>

            {/* Per-day chart */}
            <div className="border border-white/10 bg-[#0f0f0f] p-6 md:p-8">
                <h3 className="text-xs uppercase tracking-[0.22em] text-zinc-500 mb-6">Évolution — 30 derniers jours</h3>
                {data.per_day.length === 0 ? (
                    <p className="text-zinc-500 text-sm">Aucune visite enregistrée pour l'instant.</p>
                ) : (
                    <div className="flex items-end gap-1 h-40">
                        {data.per_day.map((d) => (
                            <div key={d.date} className="flex-1 flex flex-col items-center group">
                                <div
                                    className="w-full bg-[#c5a059]/30 hover:bg-[#c5a059] transition-colors relative"
                                    style={{ height: `${(d.count / max) * 100}%`, minHeight: "2px" }}
                                    title={`${d.date} — ${d.count}`}
                                >
                                    <span className="absolute -top-6 left-1/2 -translate-x-1/2 text-xs text-[#c5a059] opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                                        {d.count}
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
                {data.per_day.length > 0 && (
                    <div className="flex justify-between text-xs text-zinc-600 mt-3">
                        <span>{data.per_day[0]?.date}</span>
                        <span>{data.per_day[data.per_day.length - 1]?.date}</span>
                    </div>
                )}
            </div>

            {/* Top pages + Referrers */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="border border-white/10 bg-[#0f0f0f] p-6 md:p-8">
                    <h3 className="text-xs uppercase tracking-[0.22em] text-zinc-500 mb-6">Pages les plus visitées</h3>
                    {data.top_pages.length === 0 ? (
                        <p className="text-zinc-500 text-sm">Pas de données.</p>
                    ) : (
                        <ul className="space-y-3">
                            {data.top_pages.map((p) => {
                                const pct = data.top_pages[0].count ? (p.count / data.top_pages[0].count) * 100 : 0;
                                return (
                                    <li key={p.path}>
                                        <div className="flex justify-between text-sm mb-1">
                                            <span className="text-zinc-200">{PATH_LABEL[p.path] || p.path}</span>
                                            <span className="text-[#c5a059] font-medium">{p.count}</span>
                                        </div>
                                        <div className="h-1 bg-white/5">
                                            <div className="h-full bg-[#c5a059]" style={{ width: `${pct}%` }} />
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>

                <div className="border border-white/10 bg-[#0f0f0f] p-6 md:p-8">
                    <h3 className="text-xs uppercase tracking-[0.22em] text-zinc-500 mb-6">D'où viennent vos visiteurs ?</h3>
                    {data.top_referrers.length === 0 ? (
                        <div className="text-zinc-500 text-sm space-y-2">
                            <p>Pas encore de source identifiée.</p>
                            <p className="text-xs">Les visiteurs arrivent directement (lien partagé, tapé, ou Instagram bio).</p>
                        </div>
                    ) : (
                        <ul className="space-y-3">
                            {data.top_referrers.map((r) => {
                                let host = r.referrer;
                                try { host = new URL(r.referrer).hostname; } catch (_e) { /* keep raw */ }
                                return (
                                    <li key={r.referrer} className="flex justify-between items-center text-sm">
                                        <a href={r.referrer} target="_blank" rel="noopener noreferrer" className="text-zinc-200 hover:text-[#c5a059] flex items-center gap-2 truncate">
                                            <ExternalLink size={12} strokeWidth={1.5} />
                                            <span className="truncate">{host}</span>
                                        </a>
                                        <span className="text-[#c5a059] font-medium ml-3">{r.count}</span>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>
            </div>

            <p className="text-xs text-zinc-600 text-center">
                ⓘ Les statistiques excluent vos visites en tant qu'admin et les pages /admin/*.
            </p>
        </div>
    );
}
